import type { User } from '@creator/sdk/modules/account/account.model';
import type { GetUsersInfoFilterBy } from '@creator/sdk/modules/admin/admin.service';
import Spinner from '@creator/ui/components/Spinner/Spinner';
import Table from '@creator/ui/components/Table/Table';
import Tbody from '@creator/ui/components/Table/Tbody/Tbody';
import Td from '@creator/ui/components/Table/Td/Td';
import Th from '@creator/ui/components/Table/Th/Th';
import Thead from '@creator/ui/components/Table/Thead/Thead';
import Tr from '@creator/ui/components/Table/Tr/Tr';
import Text from '@creator/ui/components/Text/Text';
import { convertFirebaseTimestampToDate, toCustomLocaleDateYearMonthDay } from '@creator/ui/utils/date-utils';
import { cn } from '@creator/ui/utils/ui';
import { getQueryParamFromLocation } from '@creator/ui/utils/url-utils';
import Icon from '@src/basic-components/Icon/Icon';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { useStoreActions } from '@src/model/hooks';
import { roundToDecimalDigits } from '@src/utils/number-utils/number-utils';
import { noop } from '@src/utils/utils';
import type { DocumentSnapshot } from 'firebase/firestore/lite';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useLocation, useNavigate } from 'react-router';

const PAGE_SIZE = 40;

export interface TokenPageHoldersProps {
    tokenName: string;
    className?: string;
}

// Only keep totalFollowers and createdAt for ordering
const ORDER_BY_TYPES = ['totalFollowers', 'createdAt'];

// Keep filtering by user type
const FILTER_BY_USER_TYPE = ['all', 'partner'];

const TokenPageHoldersTab: FC<TokenPageHoldersProps> = props => {
    const { tokenName, className = '' } = props;
    const location = useLocation();
    const orderByFilter = getQueryParamFromLocation(location.search, 'orderBy') || ORDER_BY_TYPES[0];
    const typeFilter = getQueryParamFromLocation(location.search, 'type') || FILTER_BY_USER_TYPE[0];

    const { t } = useTranslation();
    const loadUsers = useStoreActions(actions => actions.user.loadUsers);

    const navigate = useNavigate();

    const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
    const [hasMoreUsers, setHasMoreUsers] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [usersList, setUsersList] = useState<User[]>([]);

    // For dropdown toggles
    const [isOrderByFilterOpen, setIsOrderByFilterOpen] = useState(false);
    const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);

    // Load users whenever orderByFilter / typeFilter changes
    useEffect(() => {
        loadUsersList();
    }, [orderByFilter, typeFilter]);

    function onOrderByFilterChange(filter: string): void {
        navigate(`/${ensureTokenUrl(tokenName)}/tab/holders?orderBy=${filter}&type=${typeFilter}`);
        setIsOrderByFilterOpen(false);
    }

    function onTypeFilterChange(filter: string): void {
        navigate(`/${ensureTokenUrl(tokenName)}/tab/holders?orderBy=${orderByFilter}&type=${filter}`);
        setIsTypeFilterOpen(false);
    }

    // Build filter array for the user store queries
    function getFilterByType(): GetUsersInfoFilterBy[] {
        switch (typeFilter) {
            case 'partner':
                return [{ field: `enableSell.${tokenName}`, opStr: '==', value: true }];
            case 'banned':
                return [{ field: `bannedCommunities.${tokenName}`, opStr: '==', value: true }];
            default:
                // 'all'
                return [{ field: 'joinedCommunities', opStr: 'array-contains', value: tokenName }];
        }
    }

    async function loadUsersList(lowerBound?: DocumentSnapshot): Promise<void> {
        if (!lowerBound) {
            setUsersList([]);
            setLastDoc(null);
        }

        setIsLoadingUsers(true);

        const filter = getFilterByType();
        const response = await loadUsers({
            limit: PAGE_SIZE,
            filterBy: filter,
            orderBy: typeFilter === 'all' ? { direction: 'desc', field: orderByFilter } : null,
            lowerBound: lowerBound || null,
        });

        setHasMoreUsers(response.hasMore);
        setLastDoc(response.lastDoc);

        const newUsersList = [...(lowerBound ? usersList : []), ...response.items];
        setUsersList(newUsersList);
        setIsLoadingUsers(false);
    }

    async function onLoadMore() {
        if (isLoadingUsers) return;
        loadUsersList(lastDoc || undefined);
    }

    function renderName(user: User) {
        const { displayName, profileName, slugUrl } = user;
        return (
            <Td>
                <div className="flex items-center">
                    <ProfileImage className="w-8 h-8" displayName={displayName} />
                    <Text className="ml-2 cursor-pointer" onClick={() => navigate(`/${ensureTokenUrl(tokenName)}/author/${slugUrl || displayName}`)}>
                        {profileName || displayName}
                    </Text>
                </div>
            </Td>
        );
    }

    function renderFollowers(amount: number) {
        return (
            <Td>
                <Text>{roundToDecimalDigits(amount, 0)}</Text>
            </Td>
        );
    }

    function renderJoined(createdAt?: any) {
        if (!createdAt)
            return (
                <Td>
                    <Text>-</Text>
                </Td>
            );

        return (
            <Td>
                <Text>{toCustomLocaleDateYearMonthDay(convertFirebaseTimestampToDate(createdAt))}</Text>
            </Td>
        );
    }

    function renderRow(user: User, i: number) {
        if (!user.displayName.includes('@')) return null;

        return (
            <Tr key={i}>
                {renderName(user)}
                {renderFollowers(user.totalFollowers || 0)}
                {renderJoined(user.createdAt)}
            </Tr>
        );
    }

    function renderTableBody() {
        if (!usersList || usersList.length === 0) return null;
        return usersList.map((user, index) => renderRow(user, index));
    }

    function renderOrderByFilter() {
        if (!isOrderByFilterOpen) return null;

        return (
            <div className="absolute z-10 flex flex-col w-full bg-white rounded-lg shadow-sm dark:bg-gray-950">
                {ORDER_BY_TYPES.map(type => (
                    <Text
                        key={type}
                        onClick={() => onOrderByFilterChange(type)}
                        className={`pl-4 rounded-lg ${
                            orderByFilter === type ? 'text-gray-900 dark:text-gray-50 font-bold bg-primary-100 dark:bg-primary-900' : 'text-gray-500 dark:text-gray-300'
                        } pt-3 pb-3 cursor-pointer`}
                    >
                        {t(`${type}HoldersOrderBy`)}
                    </Text>
                ))}
            </div>
        );
    }

    function renderTypeFilter() {
        if (!isTypeFilterOpen) return null;

        return (
            <div className="absolute z-10 flex flex-col w-full bg-white rounded-lg shadow-sm dark:bg-gray-950">
                {FILTER_BY_USER_TYPE.map(type => (
                    <Text
                        key={type}
                        onClick={() => onTypeFilterChange(type)}
                        className={`pl-4 rounded-lg ${
                            typeFilter === type ? 'text-gray-900 dark:text-gray-50 font-bold bg-primary-100 dark:bg-primary-900' : 'text-gray-500 dark:text-gray-300'
                        } pt-3 pb-3 cursor-pointer`}
                    >
                        {t(`${type}UsersOrderBy`)}
                    </Text>
                ))}
            </div>
        );
    }

    function renderFilters() {
        // e.g., "allUsersOrderBy" label
        const orderByUser = t(`${typeFilter}UsersOrderBy`);
        // e.g., "totalFollowersHoldersOrderBy" or "createdAtHoldersOrderBy"
        const orderBy = t(`${orderByFilter}HoldersOrderBy`);

        // If typeFilter != all, we still allow changing the order, but you can disable if needed
        const isSortDisabled = false;

        return (
            <div className="flex items-center">
                <div className="relative mr-6">
                    <div
                        onClick={() => setIsTypeFilterOpen(!isTypeFilterOpen)}
                        style={{ width: '176px', height: '40px' }}
                        className="flex items-center justify-between p-3 text-sm rounded-md shadow-sm cursor-pointer dark:shadow-sm"
                    >
                        <Text>{orderByUser}</Text>
                        <Icon className="text-gray-500 dark:text-gray-300" name="caretDown" />
                    </div>
                    {renderTypeFilter()}
                </div>
                <div className="relative">
                    <div
                        onClick={isSortDisabled ? () => noop : () => setIsOrderByFilterOpen(!isOrderByFilterOpen)}
                        style={{ width: '176px', height: '40px' }}
                        className={`flex p-3 text-sm shadow-sm dark:shadow-sm rounded-md justify-between items-center ${isSortDisabled ? 'cursor-pointer' : 'cursor-pointer'}`}
                    >
                        <Text>{t('orderHoldersBy', { orderByFilter: orderBy })}</Text>
                        <Icon className="text-gray-500 dark:text-gray-300" name="caretDown" />
                    </div>
                    {renderOrderByFilter()}
                </div>
            </div>
        );
    }

    function renderTableHeaderCell(type: string) {
        return (
            <Th>
                <div className="flex items-center">
                    <Text>{t(`holdersTab.tableHeader.${type}`)}</Text>
                </div>
            </Th>
        );
    }

    function renderTableHeader() {
        return (
            <Tr>
                {renderTableHeaderCell('name')}
                {renderTableHeaderCell('followers')}
                {renderTableHeaderCell('joined')}
            </Tr>
        );
    }

    function renderTable() {
        return (
            <Table className="w-full">
                <Thead>{renderTableHeader()}</Thead>
                <Tbody className="font-medium">{renderTableBody()}</Tbody>
            </Table>
        );
    }

    return (
        <div className={cn('w-full p-4 mb-6', className)}>
            <div className="p-2 px-4">{renderFilters()}</div>
            <InfiniteScroll initialLoad={false} loadMore={onLoadMore} hasMore={hasMoreUsers}>
                {renderTable()}
                {isLoadingUsers && <Spinner className="mx-auto mt-4" />}
            </InfiniteScroll>
        </div>
    );
};

export default TokenPageHoldersTab;
